export const S3Image = (fileName) => `https://s3-us-west-2.amazonaws.com/findbob-production/images/${fileName}`;

// Base path for new avatar system
const AVATARS_BASE_PATH = 'https://findbob-production.s3.us-west-2.amazonaws.com/images/avatars';

/**
 * Check if a filename is a new-style avatar (avatar_XXX format)
 * @param {string} fileName - The avatar filename
 * @returns {boolean} True if it's a new-style avatar
 */
export const isNewStyleAvatar = (fileName) => {
  if (!fileName) return false;
  const value = String(fileName);

  // Direct filename format: avatar_001 or avatar_001.png
  if (/^avatar_\d{3}(\.png)?$/.test(value)) return true;

  // Full URL/path format ending in avatar_001.png (optionally with query params)
  const withoutQuery = value.split('?')[0];
  if (/\/avatar_\d{3}\.png$/i.test(withoutQuery)) return true;

  return false;
};

/**
 * Get the full URL for an avatar image
 * @param {string} fileName - The avatar filename (e.g., 'avatar_001.png' or 'rj-1-rectangle.png')
 * @param {object} companySettings - Company settings object with avatar config
 * @param {string} companySettings.avatar_directory - Company-specific S3 directory (optional)
 * @returns {string} Full S3 URL for the avatar
 */
export const getAvatarUrl = (fileName, companySettings = {}) => {
  if (!fileName) return S3Image('advisor.png');
  const name = typeof fileName === 'string' ? fileName : String(fileName);
  if (name.startsWith('http://') || name.startsWith('https://')) return name;

  if (isNewStyleAvatar(name)) {
    // Ensure filename has .png extension
    const normalizedFileName = name.endsWith('.png') ? name : `${name}.png`;
    
    // Check for company-specific directory
    if (companySettings.avatar_directory) {
      // Validate directory to prevent path traversal
      const safeDirectory = companySettings.avatar_directory.replace(/[^a-zA-Z0-9_-]/g, '');
      return `${AVATARS_BASE_PATH}/${safeDirectory}/${normalizedFileName}`;
    }
    
    // Default avatars path: /images/avatars/avatar_XXX.png
    return `${AVATARS_BASE_PATH}/${normalizedFileName}`;
  }

  // Legacy: use old S3Image path for rectangle avatars
  return S3Image(name);
};

/**
 * Get the background color for an avatar based on company settings
 * @param {string} fileName - The avatar filename
 * @param {object} companySettings - Company settings object
 * @param {string} companySettings.avatar_background_color - Custom background color
 * @param {string} companySettings.primary_color - Fallback to primary color
 * @returns {string} CSS color value for the background
 */
export const getAvatarBackgroundColor = (fileName, companySettings = {}) => {
  // Only apply background color for new-style avatars
  if (!isNewStyleAvatar(fileName)) {
    return 'transparent';
  }
  
  // Use company's avatar background color if set
  if (companySettings.avatar_background_color) {
    return companySettings.avatar_background_color;
  }
  
  // Fallback to primary color at 20% opacity
  if (companySettings.primary_color) {
    return `${companySettings.primary_color}20`;
  }
  
  // Default light blue for new avatars
  return '#E8F4FD';
};

/**
 * Get complete avatar styling info in one call
 * @param {string} fileName - The avatar filename
 * @param {object} companySettings - Company settings object
 * @returns {object} Object with url, backgroundColor, and isNewStyle
 */
export const getAvatarStyle = (fileName, companySettings = {}) => {
  const isNew = isNewStyleAvatar(fileName);
  
  return {
    url: getAvatarUrl(fileName, companySettings),
    backgroundColor: getAvatarBackgroundColor(fileName, companySettings),
    isNewStyle: isNew,
  };
};
